import {
  Package, Calendar, MapPin, CheckCircle, Warning,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { useI18n } from "../lib/i18n";
import { fmtEur, fmtDate, countryFlag } from "../lib/utils";

/**
 * Shared Order Detail body - identical UI between Client and Admin.
 * Wrap this inside a BottomSheet from both sides.
 */
export default function OrderDetailBody({ order, lang, extraChip = null, mapSkuUrl = null }) {
  const { t } = useI18n();
  const cost = order.cost || {};
  const podTotal = (cost.items_total || 0) - (cost.picking_fee || 0);
  const breakdown = (cost.breakdown || []).filter((b) => !b.is_picking);
  const csvRows = order.csv_rows || [];
  const isBlocked = order.status === "blocked";
  const isUnknownSku = isBlocked && order.block_reason === "unknown_sku";

  return (
    <div className="space-y-6" data-testid="order-detail-body">
      {isUnknownSku && (
        <div className="tf-card p-4 border-2 border-[var(--tf-error)] bg-[#FBDDE1]/50" data-testid="unknown-sku-alert">
          <div className="flex items-center gap-2 font-display font-bold text-sm text-[var(--tf-error)]">
            <Warning size={18} weight="fill" /> {t("order_blocked_unknown_sku_title")}
          </div>
          <p className="text-xs mt-1.5">
            {t("order_blocked_unknown_sku_body", { codes: (order.unknown_skus || []).join(", ") })}
          </p>
          {mapSkuUrl && (
            <Link to={mapSkuUrl} className="tf-btn tf-btn-primary mt-3 inline-flex" data-testid="map-sku-now-link">
              {t("order_map_now")}
            </Link>
          )}
        </div>
      )}

      {/* Info summary */}
      <div className="grid grid-cols-2 gap-3">
        <div className="tf-card p-3">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[var(--tf-muted)] font-semibold">
            <Calendar size={11} weight="bold" /> {t("order_date")}
          </div>
          <div className="font-display font-bold text-sm mt-1">{fmtDate(order.myze_created_at || order.created_at, lang)}</div>
        </div>
        <div className="tf-card p-3">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[var(--tf-muted)] font-semibold">
            <MapPin size={11} weight="bold" /> {t("order_destination")}
          </div>
          <div className="font-display font-bold text-sm mt-1 font-mono">
            {order.shipping_country ? `${countryFlag(order.shipping_country)} ${order.shipping_country}` : "—"}
          </div>
        </div>
        <div className="tf-card p-3">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[var(--tf-muted)] font-semibold">
            <Package size={11} weight="bold" /> {t("order_myze_id")}
          </div>
          <div className="font-mono text-xs mt-1 truncate">{order.external_order_number || order.myze_order_id}</div>
        </div>
        <div className="tf-card p-3">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[var(--tf-muted)] font-semibold">
            {isBlocked ? <Warning size={11} weight="bold" /> : <CheckCircle size={11} weight="bold" />} {t("order_status")}
          </div>
          <div className="mt-1">
            <span className={`tf-status-pill ${isBlocked ? "tf-status-error" : "tf-status-success"}`}>
              {isBlocked ? t("blocked") : t("order_paid")}
            </span>
          </div>
        </div>
      </div>

      {(order.customer_name || extraChip) && (
        <div className="tf-card p-4 bg-[var(--tf-bg)] space-y-2">
          {extraChip}
          {order.customer_name && (
            <div>
              <div className="tf-label">{t("order_final_customer")}</div>
              <div className="font-display font-bold text-base mt-1">{order.customer_name}</div>
            </div>
          )}
        </div>
      )}

      {/* Articoli */}
      <section>
        <div className="tf-label flex items-center gap-2 mb-3"><Package size={14} weight="bold" /> {t("order_articles")}</div>
        <div className="space-y-2">
          {csvRows.length === 0 && (
            <div className="tf-card p-4 text-center text-[var(--tf-muted)] text-sm">
              {t("order_articles_missing")}
            </div>
          )}
          {csvRows.map((r, i) => {
            const isPod = (r.mapped_codes || []).some((m) => m.is_pod);
            const isPremade = (r.mapped_codes || []).some((m) => !m.is_pod);
            const tipo = isPod && !isPremade ? t("order_type_pod")
                       : !isPod && isPremade ? t("order_type_premade")
                       : isPod && isPremade ? t("order_type_pod_premade")
                       : "—";
            return (
              <div key={r.row_id || i} className="tf-card p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-bold text-sm truncate">{r.product_name || "—"}</div>
                    <div className="text-[11px] text-[var(--tf-muted)] mt-1 font-mono truncate">{r.product_sku || "—"}</div>
                  </div>
                  <span className={`tf-status-pill ${
                    tipo === t("order_type_pod") ? "tf-status-success"
                    : tipo === t("order_type_premade") ? "tf-status-info"
                    : "tf-status-neutral"
                  } flex-shrink-0`}>{tipo}</span>
                </div>
                <div className="flex items-center gap-3 mt-2 text-[11px] text-[var(--tf-muted)] flex-wrap">
                  {r.product_colour && <span><b className="text-[var(--tf-ink)]">{t("order_color")}:</b> {r.product_colour}</span>}
                  {r.product_size && <span><b className="text-[var(--tf-ink)]">{t("order_size")}:</b> {r.product_size}</span>}
                  {r.print_faces && <span><b className="text-[var(--tf-ink)]">{t("order_side")}:</b> {r.print_faces}</span>}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Voci di costo */}
      <section>
        <div className="tf-label mb-3">{t("order_cost_items_title")}</div>
        <div className="tf-card overflow-hidden">
          {breakdown.length === 0 && (
            <div className="p-4 text-center text-[var(--tf-muted)] text-sm">{t("order_no_pod_items")}</div>
          )}
          {breakdown.map((b, i) => (
            <div key={i} className={`flex items-center justify-between p-3 ${i > 0 ? "border-t border-[var(--tf-border)]" : ""}`}>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">
                  {b.code === "_branding_fee" && b.addon_types
                    ? `${t("order_branding_fee_label")} ${b.addon_types.map((c) => t(c === "sticker" ? "brand_tab_sticker" : "brand_tab_tyc")).join(" + ")}`
                    : (b.label || b.code)}
                </div>
                <div className="text-[11px] text-[var(--tf-muted)] mt-0.5 tf-numeric">
                  {b.code === "_branding_fee" && b.qty > 1
                    ? Array.from({ length: b.qty }).map(() => fmtEur(b.unit_price)).join(" + ")
                    : `${b.qty} × ${fmtEur(b.unit_price)}`}
                </div>
              </div>
              <div className="tf-numeric font-bold text-sm">{fmtEur(b.total)}</div>
            </div>
          ))}
          {(cost.picking_fee || 0) > 0 && (
            <div className="flex items-center justify-between p-3 bg-[var(--tf-bg)] border-t border-[var(--tf-border)]">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-bold">{t("order_picking_label")}</div>
                <div className="text-[11px] text-[var(--tf-muted)] mt-0.5">{t("order_picking_sub", { n: cost.premade_qty })}</div>
              </div>
              <div className="tf-numeric font-bold text-sm">{fmtEur(cost.picking_fee)}</div>
            </div>
          )}
          <div className="flex items-center justify-between p-3 bg-[var(--tf-bg)] border-t border-[var(--tf-border)]">
            <div className="min-w-0 flex-1">
              <div className="text-sm font-bold">{t("order_shipping_label")}</div>
              <div className="text-[11px] text-[var(--tf-muted)] mt-0.5">{cost.shipping_zone || "IT"} · {cost.total_qty} {t("unit_pcs")}</div>
            </div>
            <div className="tf-numeric font-bold text-sm">{fmtEur(cost.shipping)}</div>
          </div>
        </div>
      </section>

      {/* Totali con scorporo IVA */}
      <section className="tf-card p-5 bg-[#F5F2E8] space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <div className="text-center">
            <div className="text-[10px] uppercase tracking-widest text-[var(--tf-muted)] font-semibold">{t("order_pod_short")}</div>
            <div className="font-display font-bold text-lg mt-1 tf-numeric text-[var(--tf-ink)]">{fmtEur(podTotal)}</div>
          </div>
          <div className="text-center">
            <div className="text-[10px] uppercase tracking-widest text-[var(--tf-muted)] font-semibold">{t("order_picking_short")}</div>
            <div className="font-display font-bold text-lg mt-1 tf-numeric text-[var(--tf-ink)]">{fmtEur(cost.picking_fee || 0)}</div>
          </div>
          <div className="text-center">
            <div className="text-[10px] uppercase tracking-widest text-[var(--tf-muted)] font-semibold">{t("order_shipping_short")}</div>
            <div className="font-display font-bold text-lg mt-1 tf-numeric text-[var(--tf-ink)]">{fmtEur(cost.shipping || 0)}</div>
          </div>
        </div>

        <div className="pt-3 border-t border-[var(--tf-border)] space-y-2.5">
          {/* Imponibile row */}
          <div className="flex items-center justify-between gap-3">
            <div className="text-[10px] uppercase tracking-widest text-[var(--tf-muted)] font-semibold">
              {t("order_taxable")}
            </div>
            <div className="font-display font-bold text-lg tf-numeric text-[var(--tf-ink)] text-right">
              {fmtEur(cost.imponibile ?? cost.total ?? 0)}
            </div>
          </div>

          {/* IVA row */}
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[var(--tf-muted)] font-semibold">
                {t("order_vat", { rate: cost.vat_rate != null ? cost.vat_rate : 22 })}
              </div>
              {cost.vat_regime && cost.vat_regime !== "it" && (
                <div className="text-[10px] text-[var(--tf-muted)] mt-0.5">
                  {cost.vat_regime === "eu_rc" ? t("order_reverse_charge") : t("order_not_subject")}
                </div>
              )}
            </div>
            <div className="font-display font-bold text-lg tf-numeric text-[var(--tf-ink)] text-right">
              {fmtEur(cost.vat_amount || 0)}
            </div>
          </div>
        </div>

        {/* TOTALE SCALATO - focal point */}
        <div className="mt-2 pt-4 border-t-2 border-[var(--tf-ink)] flex items-end justify-between gap-3">
          <div className="text-[11px] md:text-xs uppercase tracking-widest text-[var(--tf-ink)] font-black">
            {t("order_total_charged")}
          </div>
          <div
            className="font-display font-black text-3xl md:text-4xl tf-numeric text-[var(--tf-ink)] text-right"
            style={{ textShadow: "none", WebkitTextStroke: 0 }}
            data-testid="total-scalato"
          >
            {fmtEur(cost.total_ivato ?? cost.total ?? 0)}
          </div>
        </div>
        <div className="text-[10px] text-[var(--tf-muted)] text-right">
          {t("order_total_charged_note")}
        </div>
      </section>
    </div>
  );
}
